import { useState } from "react";
import { allRegions, allYears, allTypes } from "@/utils/data";

export const ratingOptions = [
  { label: "不限", value: "全部" },
  { label: "9 分以上", value: "9" },
  { label: "8 分以上", value: "8" },
  { label: "7.5 分以上", value: "7.5" },
];

export const sortOptions = [
  { label: "按热门度", value: "popular" },
  { label: "按最新", value: "latest" },
  { label: "按评分", value: "rating" },
];

interface FilterBarProps {
  type: string;
  region: string;
  year: string;
  rating: string;
  sort: string;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
  onSortChange: (value: string) => void;
}

interface ChipGroupProps {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}

function ChipGroup({ label, options, value, onChange }: ChipGroupProps) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/60 py-3.5 last:border-0 md:flex-row md:items-center md:gap-4">
      <span className="w-16 shrink-0 text-xs font-medium text-foreground-500">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                active
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-secondary-100 text-foreground-600 hover:bg-secondary-200"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  type,
  region,
  year,
  rating,
  sort,
  onChange,
  onReset,
  onSortChange,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const typeOptions = [
    { label: "不限", value: "全部" },
    ...allTypes.map((t) => ({ label: t, value: t })),
  ];
  const regionOptions = [
    { label: "不限", value: "全部" },
    ...allRegions.map((r) => ({ label: r, value: r })),
  ];
  const yearOptions = [
    { label: "不限", value: "全部" },
    ...allYears.map((y) => ({ label: `${y} 年`, value: String(y) })),
  ];

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-sm text-foreground-700"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-filter-3-line" />
          </span>
          筛选条件
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </span>
        </button>
        <button
          type="button"
          onClick={onReset}
          className="whitespace-nowrap text-sm text-foreground-500"
        >
          重置
        </button>
      </div>

      <div
        className={`${open ? "mt-3 block" : "hidden"} md:block md:mt-0`}
      >
        <ChipGroup
          label="类型"
          options={typeOptions}
          value={type}
          onChange={(v) => onChange("type", v)}
        />
        <ChipGroup
          label="年代"
          options={yearOptions}
          value={year}
          onChange={(v) => onChange("year", v)}
        />
        <ChipGroup
          label="地区"
          options={regionOptions}
          value={region}
          onChange={(v) => onChange("region", v)}
        />
        <ChipGroup
          label="评分"
          options={ratingOptions}
          value={rating}
          onChange={(v) => onChange("rating", v)}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200/60 pt-4">
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-refresh-line" />
            </span>
            重置筛选
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-500">排序</span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((option) => {
              const active = option.value === sort;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSortChange(option.value)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                    active
                      ? "bg-accent-500 font-medium text-background-50"
                      : "bg-secondary-100 text-foreground-600 hover:bg-secondary-200"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}